import { useState, useMemo, useCallback } from 'react';
import {
  InsightCardMultiPageRenderer,
  InsightCardBlockRenderer,
  ReportBlocksRenderer,
} from '@/components/ai-elements/block-renderers';
import { isInsightCardBlock } from '@/components/ai-elements/block-renderers/slots/shared';
import { TimeTravelDatePicker } from '@/components/reports/TimeTravelDatePicker';
import type { TimeTravelDateState } from '@/components/reports/TimeTravelDatePicker';
import { useTimeTravelMetrics } from '@/hooks/useTimeTravelMetrics';
import { useKeywordTimeTravelMetrics } from '@/hooks/useKeywordTimeTravelMetrics';
import { TableBlockAddonProvider } from '@/contexts/TableBlockAddonContext';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Badge,
} from '@strivelabs/ui-core';
import { RichTextRenderer, AgentThoughtsCard } from '@strivelabs/ui-blocks';
import type { AgentStep } from '@strivelabs/ui-blocks';
import type { PageGroup, Page, UIActionEvent, DataTableClientBlock, InsightCardBlock, ReportBlock, PlanCardBlock, PlanCardContent } from '@/types/ui-blocks';
import mockReportJson from './google-ads-report-data-sample.json';

/**
 * Toggle to `true` to load the local mock JSON instead of real API data.
 * Commit with `false` so production always uses live data.
 */
const USE_MOCK_DATA = false;

const CAMPAIGN_TAB_LABELS = ['Key Findings & Recommendations', 'Campaign Metrics', 'Keyword Metrics'];

interface PeriodMeta {
  current_period_start_date: string;
  current_period_end_date: string;
  baseline_period_start_date: string;
  baseline_period_end_date: string;
}

/** Check if a data_table_client block is a campaign metrics comparison table (not keywords, etc.) */
function isCampaignMetricsTable(block: { content: { columns?: Array<{ key: string }> } }): boolean {
  return block.content.columns?.some(col => col.key === 'baseline_avg') ?? false;
}

/** Check if a data_table_client block is a keyword table (has keyword column) */
function isKeywordTable(block: { content: { columns?: Array<{ key: string }> } }): boolean {
  return block.content.columns?.some(col => col.key === 'keyword') ?? false;
}

/**
 * Attempt to swap a data_table_client block with time-travel data.
 * Returns the replacement block if matched, or null if no swap applies.
 */
function swapTableBlock(
  blockLike: { type: string; content: { columns?: Array<{ key: string }> } },
  timeTravelData: unknown,
  keywordTimeTravelData: unknown,
  flags: { campaignReplaced: boolean; keywordReplaced: boolean }
): { swapped: unknown; flag: 'campaign' | 'keyword' } | null {
  if (blockLike.type !== 'data_table_client') return null;

  if (!flags.campaignReplaced && timeTravelData && isCampaignMetricsTable(blockLike)) {
    return { swapped: { ...blockLike, content: timeTravelData }, flag: 'campaign' };
  }
  if (!flags.keywordReplaced && keywordTimeTravelData && isKeywordTable(blockLike)) {
    return { swapped: { ...blockLike, content: keywordTimeTravelData }, flag: 'keyword' };
  }
  return null;
}

/** Check if a report block is a plan_card payload. */
function isPlanCardBlock(block: ReportBlock): block is PlanCardBlock {
  return 'type' in block && block.type === 'plan_card';
}

/** Map PlanCardContent steps → AgentStep[] for AgentThoughtsCard. */
function mapPlanCardStepsToAgentSteps(steps: PlanCardContent['steps']): AgentStep[] {
  return steps.map(step => ({
    id: step.task_id,
    title: step.title,
    status: step.status,
    content: { type: 'summary' as const, body: step.description ?? '' },
  }));
}

interface GoogleAdsReportViewerProps {
  pageGroups: PageGroup[];
  metadata: Record<string, unknown>;
  onUIAction?: (event: UIActionEvent) => void;
}

export function GoogleAdsReportViewer({ pageGroups: pageGroupsProp, metadata: metadataProp, onUIAction }: GoogleAdsReportViewerProps) {
  const pageGroups: PageGroup[] = USE_MOCK_DATA ? ((mockReportJson as Record<string, unknown>).page_groups as PageGroup[] ?? pageGroupsProp) : pageGroupsProp;
  const metadata: Record<string, unknown> = USE_MOCK_DATA ? ((mockReportJson as Record<string, unknown>).metadata as Record<string, unknown> ?? metadataProp) : metadataProp;
  // Per-page date state, keyed by page ID
  const [dateStateByPage, setDateStateByPage] = useState<Record<string, TimeTravelDateState | null>>({});

  // Track the currently active page via the onActivePageChange callback
  const [activePage, setActivePage] = useState<Page | null>(null);

  const googleAdsDatasetId = metadata?.google_ads_dataset_id as string | undefined;
  const periodMeta = metadata?.period_meta as PeriodMeta | undefined;

  const activePageId = activePage?.id ?? null;
  const activeCampaignId = activePage?.metadata?.campaign_id as string | undefined;
  const activeDateState = activePageId ? dateStateByPage[activePageId] ?? null : null;

  // Build params for the hook
  // current_period comes from report metadata (fixed), baseline_period from date picker (user-chosen)
  const timeTravelParams = useMemo(() => {
    if (!googleAdsDatasetId || !activeCampaignId || !activeDateState || !periodMeta) return null;
    return {
      google_ads_dataset_id: googleAdsDatasetId,
      campaign_id: activeCampaignId,
      current_period_start_date: periodMeta.current_period_start_date,
      current_period_end_date: periodMeta.current_period_end_date,
      baseline_period_start_date: activeDateState.currentPeriod.startDate,
      baseline_period_end_date: activeDateState.currentPeriod.endDate,
    };
  }, [googleAdsDatasetId, activeCampaignId, activeDateState, periodMeta]);

  const { data: timeTravelData, isLoading: isTimeTravelLoading } = useTimeTravelMetrics(
    timeTravelParams,
    !!timeTravelParams
  );

  const { data: keywordTimeTravelData, isLoading: isKeywordTimeTravelLoading } = useKeywordTimeTravelMetrics(
    timeTravelParams,
    !!timeTravelParams
  );

  // Clone pageGroups and swap campaign metrics / keyword data_table_client blocks when time-travel data is available.
  // Blocks can be nested inside InsightCardBlock.block_elements, so we traverse both levels.
  const effectivePageGroups = useMemo(() => {
    if ((!timeTravelData && !keywordTimeTravelData) || !activePageId) return pageGroups;

    return pageGroups.map(group => ({
      ...group,
      pages: group.pages.map(page => {
        if (page.id !== activePageId) return page;

        const flags = { campaignReplaced: false, keywordReplaced: false };
        return {
          ...page,
          blocks: page.blocks.map((block: ReportBlock) => {
            // Case 1: Top-level data_table_client block
            if ('type' in block && block.type === 'data_table_client') {
              const result = swapTableBlock(
                block as DataTableClientBlock, timeTravelData, keywordTimeTravelData, flags
              );
              if (result) {
                flags[`${result.flag}Replaced`] = true;
                return result.swapped as typeof block;
              }
            }

            // Case 2: data_table_client nested inside InsightCardBlock.block_elements
            if (isInsightCardBlock(block)) {
              let anySwapped = false;
              const newElements = block.block_elements.map(el => {
                const elAny = el as unknown as { type: string; content: { columns?: Array<{ key: string }> } };
                const result = swapTableBlock(elAny, timeTravelData, keywordTimeTravelData, flags);
                if (result) {
                  flags[`${result.flag}Replaced`] = true;
                  anySwapped = true;
                  return result.swapped as typeof el;
                }
                return el;
              });
              if (anySwapped) {
                return { ...block, block_elements: newElements } as typeof block;
              }
            }

            return block;
          }),
        };
      }),
    }));
  }, [pageGroups, timeTravelData, keywordTimeTravelData, activePageId]);

  // Handle date change for a specific page
  const handleDateChange = useCallback((pageId: string, state: TimeTravelDateState | null) => {
    setDateStateByPage(prev => ({ ...prev, [pageId]: state }));
  }, []);

  // Track the active page when it changes in the multi-page renderer
  const handleActivePageChange = useCallback((page: Page) => {
    setActivePage(page);
  }, []);

  // Render the date picker addon for campaign metrics and keyword tables.
  // Both share the same date state — changing either picker updates both tables.
  const renderTableAddon = useCallback((block: DataTableClientBlock) => {
    if (!activePageId || !activeCampaignId || !googleAdsDatasetId) return null;

    if (!isCampaignMetricsTable(block) && !isKeywordTable(block)) return null;

    const dateState = dateStateByPage[activePageId] ?? null;
    const showLoading = isTimeTravelLoading || isKeywordTimeTravelLoading;

    return (
      <TimeTravelDatePicker
        value={dateState}
        onChange={(state) => handleDateChange(activePageId, state)}
        isLoading={showLoading}
      />
    );
  }, [activePageId, activeCampaignId, googleAdsDatasetId, dateStateByPage, isTimeTravelLoading, isKeywordTimeTravelLoading, handleDateChange]);

  const renderTabbedPageContent = useCallback((blocks: ReportBlock[], onAction: (event: UIActionEvent) => void) => {
    const planCardBlocks = blocks.filter(isPlanCardBlock);
    const insightCards = blocks.filter(isInsightCardBlock);
    if (insightCards.length <= 1) {
      return <ReportBlocksRenderer blocks={blocks} onUIAction={onAction} />;
    }

    const summaryBlock = insightCards[0];
    const { tags, heading, heading_badge: headingBadge, ...restConfig } = summaryBlock.config;

    // First block's header is rendered above tabs, so strip it from the card
    const strippedSummaryBlock: InsightCardBlock = {
      ...summaryBlock,
      config: restConfig,
    };
    // Order: Key Findings (block 1), Campaign Metrics (block 0), Keyword Metrics (block 2)
    const tabbedCards = [insightCards[1], strippedSummaryBlock, ...insightCards.slice(2)];

    return (
      <div className="flex flex-col gap-6">
        {(tags?.length || heading) && (
          <div className="flex flex-col gap-2">
            {!!tags?.length && (
              <div className="flex flex-wrap gap-1.5">
                {tags.map((tag, index) => (
                  <Badge key={`${tag}-${index}`} variant="customGray" className="uppercase text-[10px] font-normal">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
            {heading && (
              <div className="flex items-center gap-3">
                <RichTextRenderer
                  content={heading}
                  className="[&>p]:text-[28px] [&>p]:leading-tight [&>p]:font-medium text-custom-text-secondary"
                />
                {headingBadge && (
                  <Badge variant="customGray" className="flex-shrink-0">
                    {headingBadge}
                  </Badge>
                )}
              </div>
            )}
          </div>
        )}

        {planCardBlocks.map(block => (
          <AgentThoughtsCard
            key={block.id}
            steps={mapPlanCardStepsToAgentSteps(block.content.steps)}
            label={block.content.label}
            detailButtonLabel={block.content.detail_button_label}
            dialogTitle={block.content.dialog_title}
            dialogDescription={block.content.dialog_description}
          />
        ))}

        {/* Tabbed content — use index-based values so the first tab stays
             active when switching between campaigns (block IDs change per page) */}
        <Tabs defaultValue="0" className="w-full">
          <TabsList
            className="grid w-full bg-gray-200 rounded-lg gap-1"
            style={{ gridTemplateColumns: `repeat(${tabbedCards.length}, minmax(0, 1fr))` }}
          >
            {tabbedCards.map((_block, idx) => (
              <TabsTrigger
                key={idx}
                value={idx.toString()}
                className="px-4 mb-2 text-sm font-medium rounded-md transition-all duration-200
                  data-[state=active]:bg-custom-card-bg data-[state=active]:text-gray-900 data-[state=active]:shadow-custom-card
                  data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:text-gray-900"
              >
                {CAMPAIGN_TAB_LABELS[idx] ?? `Tab ${idx + 1}`}
              </TabsTrigger>
            ))}
          </TabsList>

          {tabbedCards.map((block, idx) => (
            <TabsContent
              key={block.id}
              value={idx.toString()}
              className="mt-6 focus:outline-none"
            >
              <InsightCardBlockRenderer block={block} onUIAction={onAction} />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    );
  }, []);

  return (
    <TableBlockAddonProvider renderAddon={renderTableAddon}>
      <InsightCardMultiPageRenderer
        pageGroups={effectivePageGroups}
        onUIAction={onUIAction}
        onActivePageChange={handleActivePageChange}
        renderPageContent={renderTabbedPageContent}
        className="w-full"
      />
    </TableBlockAddonProvider>
  );
}
